/**
 * Unified Oceanographic Data Service
 * Fetches real-time ocean data from NOAA ERDDAP
 */

import { OceanographicData, GeoCoordinate } from '../types';
import { ERDDAPService } from './erddapService';
import { BoundingBox } from '../types/oceanographic';
import { logger } from '../utils/logger';

export class OceanographicDataService {
  /**
   * Get oceanographic data from ERDDAP
   */
  static async getOceanographicData(
    location: GeoCoordinate,
    gridSize: number = 1.0
  ): Promise<OceanographicData> {
    logger.info('Fetching ocean data from ERDDAP', { location, gridSize });
    
    // Create bounding box around the location
    const bounds: BoundingBox = {
      minLat: location.latitude - gridSize / 2,
      maxLat: location.latitude + gridSize / 2,
      minLon: location.longitude - gridSize / 2,
      maxLon: location.longitude + gridSize / 2
    };
    
    // Ensure bounds are valid
    bounds.minLat = Math.max(-90, bounds.minLat);
    bounds.maxLat = Math.min(90, bounds.maxLat);
    bounds.minLon = Math.max(-180, bounds.minLon);
    bounds.maxLon = Math.min(180, bounds.maxLon);
    
    try {
      const data = await ERDDAPService.getAllOceanData(bounds, gridSize / 10);
      // Convert to match OceanographicData interface
      return {
        sst: data.sst,
        chlorophyll: data.chlorophyll,
        ssh: data.ssh,
        mixedLayerDepth: data.mld,
        currents: data.currents,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      logger.error('Failed to fetch ERDDAP data', error);
      throw error;
    }
  }
  
  /**
   * Get image overlay URL for direct map visualization
   */
  static getImageOverlayURL(
    dataType: 'sst' | 'chlorophyll' | 'ssh' | 'currents',
    bounds: BoundingBox,
    options?: {
      temperatureRange?: { min: number; max: number };
      chlorophyllRange?: { min: number; max: number };
    }
  ): string {
    return ERDDAPService.getImageOverlayURL(dataType, bounds, options);
  }
  
  /**
   * Get SST overlay URL with dynamic temperature range
   * Automatically determines the best temperature range for visualization
   */
  static async getDynamicSSTOverlayURL(bounds: BoundingBox): Promise<string> {
    return ERDDAPService.getDynamicSSTOverlayURL(bounds);
  }
  
  /**
   * Get Chlorophyll overlay URL with dynamic concentration range
   * Automatically determines the best concentration range for visualization
   */
  static async getDynamicChlorophyllOverlayURL(bounds: BoundingBox): Promise<string> {
    return ERDDAPService.getDynamicChlorophyllOverlayURL(bounds);
  }
  
  /**
   * Subscribe to periodic data updates
   * Since ERDDAP doesn't support real-time subscriptions, we poll periodically
   */
  static subscribeToOceanographicData(
    location: GeoCoordinate,
    callback: (data: OceanographicData) => void,
    onError?: (error: Error) => void,
    intervalMs: number = 300000 // 5 minutes default
  ): () => void {
    // Initial fetch
    this.getOceanographicData(location)
      .then(callback)
      .catch(error => onError?.(error instanceof Error ? error : new Error(String(error))));
    
    // Set up polling interval
    const interval = setInterval(() => {
      this.getOceanographicData(location)
        .then(callback)
        .catch(error => onError?.(error instanceof Error ? error : new Error(String(error))));
    }, intervalMs);
    
    // Return unsubscribe function
    return () => {
      clearInterval(interval);
    };
  }
}